import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';

const AUTH_COOKIE_NAME = 'auth_token';
const AUTH_EXPIRY_HOURS = 6;

interface AuthContextType {
    isAuthenticated: boolean;
    token: string | null;
    login: (token: string) => void;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [token, setToken] = useState<string | null>(Cookies.get(AUTH_COOKIE_NAME) || null);
    const navigate = useNavigate();

    const login = (newToken: string) => {
        Cookies.set(AUTH_COOKIE_NAME, newToken, {
            expires: AUTH_EXPIRY_HOURS / 24,
            secure: process.env.NODE_ENV === 'production',
            sameSite: 'strict'
        });
        setToken(newToken);
    };

    const logout = () => {
        Cookies.remove(AUTH_COOKIE_NAME);
        setToken(null);
        navigate('/login');
    };

    // Check token on mount and setup periodic token check
    useEffect(() => {
        const checkToken = () => {
            const currentToken = Cookies.get(AUTH_COOKIE_NAME);
            if (!currentToken && token) {
                // Token was removed externally
                logout();
            } else if (currentToken && !token) {
                // Token was added externally
                setToken(currentToken);
            }
        };

        // Check token every minute
        const interval = setInterval(checkToken, 60000);
        return () => clearInterval(interval);
    }, [token]);

    const value = {
        isAuthenticated: !!token,
        token,
        login,
        logout
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
